import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FileText, Users, LogOut, BookTemplate, Workflow, Bot,
  Settings, MessageSquareWarning, UserCircle2, FileBarChart, PieChart, Receipt, Menu, X
} from "lucide-react";

export function AppShell({ children }) {
  const { user, tenant, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const isSuper = user?.role === "super_admin";

  // Primary tabs shown in the mobile bottom bar (max 4-5).
  const primary = isSuper
    ? [
        { to: "/admin", label: "Overview", icon: LayoutDashboard },
        { to: "/admin/tenants", label: "Tenants", icon: Users },
      ]
    : [
        { to: "/app", label: "Home", icon: LayoutDashboard },
        { to: "/app/surveys", label: "Surveys", icon: FileText },
        { to: "/app/analytics", label: "Insights", icon: PieChart },
        { to: "/app/settings", label: "Settings", icon: Settings },
      ];

  // Full list for desktop sidebar + mobile drawer.
  const all = isSuper
    ? primary
    : [
        { to: "/app", label: "Home", icon: LayoutDashboard },
        { to: "/app/surveys", label: "Surveys", icon: FileText },
        { to: "/app/templates", label: "Templates", icon: BookTemplate },
        { to: "/app/journeys", label: "Journeys", icon: Workflow },
        { to: "/app/tickets", label: "Tickets", icon: MessageSquareWarning },
        { to: "/app/customers", label: "Customers", icon: UserCircle2 },
        { to: "/app/transactions", label: "Transactions", icon: Receipt },
        { to: "/app/analytics", label: "Analytics", icon: PieChart },
        { to: "/app/reports", label: "Reports", icon: FileBarChart },
        { to: "/app/ai", label: "AI Insights", icon: Bot },
        { to: "/app/team", label: "Team", icon: Users },
        { to: "/app/settings", label: "Settings", icon: Settings },
      ];

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const isActive = (to) => {
    if (to === "/app" || to === "/admin") return loc.pathname === to;
    return loc.pathname.startsWith(to);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" style={{ backgroundColor: "var(--ink-50)" }}>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col shrink-0 bg-white border-r" style={{ borderColor: "var(--border)" }}>
        <div className="px-5 h-16 flex items-center">
          <Link to="/" data-testid="sidebar-logo"><Logo size={32} /></Link>
        </div>
        <div className="px-4 pt-4 pb-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--ink-400)" }}>
          {tenant?.name || (isSuper ? "Platform" : "Workspace")}
        </div>
        <nav className="px-2 space-y-1 flex-1">
          {all.map((it) => {
            const active = isActive(it.to);
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`sidebar-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                aria-current={active}
                className="flex items-center gap-3 px-3 py-2.5 text-[13px] font-semibold rounded-xl transition-colors"
                style={{
                  color: active ? "#FFFFFF" : "var(--ink-600)",
                  backgroundColor: active ? "var(--brand)" : "transparent",
                  boxShadow: active ? "0 8px 18px -10px rgba(245,130,32,0.55)" : "none",
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = "var(--brand-soft)"; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
              >
                <Icon size={18}/>
                {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl bg-slate-50">
            <div className="w-9 h-9 rounded-xl grid place-items-center text-xs font-black text-white" style={{ background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-strong) 100%)" }}>
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="text-[12px] leading-tight flex-1 min-w-0">
              <p className="font-bold truncate" style={{ color: "var(--ink-900)" }}>{user?.name}</p>
              <p className="text-[11px] truncate" style={{ color: "var(--ink-400)" }}>{user?.email}</p>
            </div>
          </div>
          <Button data-testid="sidebar-logout" onClick={onLogout} variant="ghost" size="sm"
            className="mt-2 w-full justify-start h-9 text-[12px] font-semibold rounded-xl"
            style={{ color: "var(--ink-500)" }}
          >
            <LogOut size={14} className="mr-2"/> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top header */}
      <header className="md:hidden sticky top-0 z-40 h-14 px-4 flex items-center justify-between bg-white/95 backdrop-blur border-b" style={{ borderColor: "var(--border)" }}>
        <Link to="/" data-testid="mobile-logo"><Logo size={28}/></Link>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setDrawerOpen(true)}
          className="w-10 h-10 rounded-xl grid place-items-center"
          style={{ backgroundColor: "var(--ink-50)", color: "var(--ink-700)" }}
          aria-label="Open menu"
        >
          <Menu size={20}/>
        </button>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)}/>
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white flex flex-col shadow-2xl">
            <div className="h-14 px-4 flex items-center justify-between border-b" style={{ borderColor: "var(--border)" }}>
              <Logo size={28}/>
              <button data-testid="mobile-menu-close" onClick={() => setDrawerOpen(false)} className="w-10 h-10 rounded-xl grid place-items-center" aria-label="Close menu"><X size={20}/></button>
            </div>
            <nav className="p-3 space-y-1 flex-1 overflow-auto">
              {all.map((it) => {
                const active = isActive(it.to);
                const Icon = it.icon;
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    data-testid={`drawer-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-current={active}
                    onClick={() => setDrawerOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-semibold"
                    style={{
                      color: active ? "#FFFFFF" : "var(--ink-700)",
                      backgroundColor: active ? "var(--brand)" : "transparent",
                    }}
                  >
                    <Icon size={18}/> {it.label}
                  </Link>
                );
              })}
            </nav>
            <div className="p-3 border-t" style={{ borderColor: "var(--border)" }}>
              <Button data-testid="drawer-logout" onClick={onLogout} variant="outline" className="w-full rounded-xl">
                <LogOut size={14} className="mr-2"/> Sign out
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="hidden md:flex h-16 px-8 items-center justify-between bg-white/60 backdrop-blur border-b sticky top-0 z-40" style={{ borderColor: "var(--border)" }}>
          <h1 className="text-[16px] font-extrabold tracking-tight" style={{ color: "var(--ink-900)" }}>{getPageTitle(loc.pathname, isSuper)}</h1>
          <div className="flex items-center gap-2 text-[11px]">
            {tenant?.plan && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold uppercase tracking-wider text-[10px]" style={{ backgroundColor: "var(--brand-soft)", color: "var(--brand-strong)" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--brand)" }}/>
                {tenant.plan}
              </span>
            )}
            <span className="px-3 py-1.5 rounded-full font-bold uppercase tracking-wider text-[10px]" style={{ backgroundColor: "var(--secondary-soft)", color: "var(--secondary)" }}>
              {user?.role?.replace("_", " ")}
            </span>
          </div>
        </header>
        <div className="flex-1 px-4 md:px-8 py-6 md:py-8 pb-24 md:pb-8 overflow-x-hidden">{children}</div>

        {/* Mobile bottom tab bar */}
        <nav className="md:hidden tabbar fixed bottom-0 left-0 right-0 z-40 px-3 py-2 flex items-center justify-around" data-testid="mobile-tabbar">
          {primary.map((it) => {
            const active = isActive(it.to);
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                aria-current={active}
                className="tabbar-item flex flex-col items-center gap-0.5 py-1 min-w-[64px]"
                data-testid={`tab-${it.label.toLowerCase()}`}
                style={{ color: active ? "var(--brand)" : "var(--ink-500)" }}
              >
                <span className="tabbar-icon-bg"><Icon size={20}/></span>
                <span className="text-[10.5px] font-bold">{it.label}</span>
              </Link>
            );
          })}
        </nav>
      </main>
    </div>
  );
}

function getPageTitle(path, isSuper) {
  const map = isSuper
    ? { "/admin": "Platform overview", "/admin/tenants": "Tenants" }
    : {
        "/app": "Home",
        "/app/surveys": "Surveys",
        "/app/templates": "Templates",
        "/app/journeys": "Journeys",
        "/app/tickets": "Closed-loop tickets",
        "/app/customers": "Customers",
        "/app/transactions": "Transactions",
        "/app/analytics": "Analytics",
        "/app/reports": "Reports",
        "/app/ai": "AI Insights",
        "/app/team": "Team",
        "/app/settings": "Settings",
      };
  const exact = map[path];
  if (exact) return exact;
  if (path.startsWith("/app/surveys/")) return "Survey";
  if (path.startsWith("/app/customers/")) return "Customer";
  return "CX10X";
}

export function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>
    );
  }
  if (!user) return <Redirect to="/login" />;
  if (role && user.role !== role) {
    return <Redirect to={user.role === "super_admin" ? "/admin" : "/app"} />;
  }
  return children;
}

function Redirect({ to }) {
  const nav = useNavigate();
  React.useEffect(() => { nav(to, { replace: true }); }, [to, nav]);
  return null;
}
